import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import WorkoutGrid from '@/components/WorkoutGrid';

const SessionPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  
  // Sensation checkpoints
  const [beforeRating, setBeforeRating] = useState(5);
  const [afterRating, setAfterRating] = useState(5);
  const [beforeNotes, setBeforeNotes] = useState('');
  const [afterNotes, setAfterNotes] = useState('');
  const [showCheckpointsBefore, setShowCheckpointsBefore] = useState(true);
  const [showCheckpointsAfter, setShowCheckpointsAfter] = useState(false);

  const handleStartSession = () => {
    setShowCheckpointsBefore(false);
    setSessionStarted(true);
  };

  const handleCompleteSession = () => {
    setSessionStarted(false);
    setSessionComplete(true);
    setShowCheckpointsAfter(true);
  };

  const handleSaveAndReturn = () => {
    // In a full implementation, this would save the progress to the backend
    navigate('/client/dashboard');
  };

  return (
    <div className="min-h-screen bg-ivory-mist text-obsidian-night" data-testid="session-page">
      {/* Header */}
      <header className="border-b border-burnt-terra/20 bg-white/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/client/dashboard')}
            className="text-shadow-pine hover:text-burnt-terra hover:bg-burnt-terra/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div>
            <h1 className="font-display text-xl font-bold text-obsidian-night">Calibration Session</h1>
            <p className="text-sm text-shadow-pine font-body">
              15 min workout • 5 min reflection
            </p>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
        {/* Before Session Checkpoint */}
        {showCheckpointsBefore && !sessionComplete && (
          <Card className="bg-white border-burnt-terra/20 shadow-xl" data-testid="before-checkpoint">
            <CardHeader>
              <CardTitle className="font-display text-obsidian-night">Before You Begin</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-obsidian-night font-body">
                  How do you feel right now? ({beforeRating}/10)
                </label>
                <Slider
                  value={[beforeRating]}
                  onValueChange={(value) => setBeforeRating(value[0])}
                  min={1}
                  max={10}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-shadow-pine/70 mt-2 font-body">
                  <span>Challenged</span>
                  <span>Aligned</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-obsidian-night font-body">Notes (optional)</label>
                <Textarea
                  placeholder="What sensations or thoughts are present?"
                  value={beforeNotes}
                  onChange={(e) => setBeforeNotes(e.target.value)}
                  rows={3}
                  className="bg-ivory-mist border-burnt-terra/30 text-obsidian-night focus:border-burnt-terra"
                />
              </div>

              <p className="text-sm text-shadow-pine/70 font-body">
                Next, you&apos;ll select your tools and begin the 15-minute calibration workout.
              </p>

              <Button
                onClick={handleStartSession}
                size="lg"
                className="w-full bg-burnt-terra text-ivory-mist hover:bg-[#A54A2F] font-semibold"
                data-testid="continue-to-workout-button"
              >
                Continue to Tool Selection
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Workout Grid */}
        {sessionStarted && !showCheckpointsAfter && (
          <Card className="bg-white border-burnt-terra/20 shadow-xl" data-testid="workout-area">
            <CardContent className="p-4">
              <WorkoutGrid 
                onComplete={handleCompleteSession}
                sessionTitle="Daily Calibration"
              />
            </CardContent>
          </Card>
        )}

        {/* After Session Checkpoint */}
        {showCheckpointsAfter && (
          <Card className="bg-white border-burnt-terra/20 shadow-xl" data-testid="after-checkpoint">
            <CardHeader>
              <CardTitle className="font-display text-obsidian-night">Session Complete - How Do You Feel?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-obsidian-night font-body">
                  Current state ({afterRating}/10)
                </label>
                <Slider
                  value={[afterRating]}
                  onValueChange={(value) => setAfterRating(value[0])}
                  min={1}
                  max={10}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-shadow-pine/70 mt-2 font-body">
                  <span>Challenged</span>
                  <span>Aligned</span>
                </div>
              </div>

              {/* Improvement indicator */}
              {afterRating > beforeRating && (
                <div className="p-4 bg-shadow-pine/10 border border-shadow-pine/30 rounded-lg">
                  <div className="text-shadow-pine font-semibold font-body">
                    +{afterRating - beforeRating} improvement
                  </div>
                  <div className="text-sm text-shadow-pine/80 mt-1 font-body">
                    You&apos;re aligned. Go build something.
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2 text-obsidian-night font-body">Reflections</label>
                <Textarea
                  placeholder="What shifted? What did you notice?"
                  value={afterNotes}
                  onChange={(e) => setAfterNotes(e.target.value)}
                  rows={4}
                  className="bg-ivory-mist border-burnt-terra/30 text-obsidian-night focus:border-burnt-terra"
                />
              </div>

              <Button
                onClick={handleSaveAndReturn}
                size="lg"
                className="w-full bg-burnt-terra text-ivory-mist hover:bg-[#A54A2F] font-semibold"
                data-testid="save-and-return-button"
              >
                Save & Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      <BrandMark />
    </div>
  );
};

export default SessionPage;
